import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

const C = {
  maroon: '#5B1818', maroonDark: '#3B0E0E', gold: '#C9A23A', goldLight: '#E2C170', goldSoft: '#F0DCA0',
  cream: '#0B0B0E', creamWarm: '#17171C', creamDeep: '#101014',
  text: '#ECE6DA', textMuted: '#A9A29A',
  border: 'rgba(201, 162, 58, 0.35)', borderStrong: 'rgba(201, 162, 58, 0.55)',
  success: '#E2C170',
};

const ThankYouPage = () => {
  const { leadId } = useParams();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [address, setAddress] = useState({ address: '', city: '', state: '', pincode: '' });
  const [addressSubmitted, setAddressSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const res = await fetch(`/api/report/${leadId}`);
        const data = await res.json();
        if (!data.error) setReportData(data);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchReport();
  }, [leadId]);

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch('/api/address/submit', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lead_id: leadId, ...address })
      });
      const data = await res.json();
      if (data.success) setAddressSubmitted(true);
    } catch (err) { console.error(err); }
    setSubmitting(false);
  };

  const pageStyle = { background: "url('/img/080857f9.webp') repeat #0B0B0E", backgroundSize: '480px', fontFamily: "'Noto Sans Devanagari', sans-serif", color: C.text, minHeight: '100vh' };
  const inputStyle = { background: '#1B1B20', color: C.text, border: `1.5px solid ${C.borderStrong}` };

  if (loading) return <div style={pageStyle} className="flex items-center justify-center"><div style={{ color: C.goldLight }} className="text-xl">Loading...</div></div>;

  return (
    <div style={pageStyle} data-testid="thank-you-page">
      <header className="py-10 px-4" style={{ background: "linear-gradient(180deg, rgba(8,8,10,0.82), rgba(8,8,10,0.88)), url('/img/d9315337.webp') center 30%/cover no-repeat", borderBottom: `2px solid ${C.gold}` }}>
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-3" style={{ background: '#6E0F0F', color: '#F5E7C8', fontSize: 32, border: '2px solid #C9A23A' }}>✓</div>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ color: C.goldSoft, fontFamily: "'Tiro Devanagari Hindi', serif" }}>भुगतान सफल</h1>
          <p className="mt-2 text-sm tracking-widest uppercase" style={{ color: C.goldLight }}>आपकी साधना शुरू होने वाली है</p>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-10">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-bold mb-2" style={{ color: C.goldLight, fontFamily: "'Tiro Devanagari Hindi', serif" }}>{reportData?.name} जी, धन्यवाद</h2>
          <p style={{ color: C.textMuted }}>आपका भुगतान सफलतापूर्वक प्राप्त हो गया है</p>
          <div className="mx-auto mt-4 h-px" style={{ width: 90, background: `linear-gradient(90deg, transparent, ${C.gold}, transparent)` }} />
        </div>

        {/* Steps */}
        <section className="rounded-2xl p-7 mb-6" style={{ background: C.creamWarm, border: `1px solid ${C.border}`, boxShadow: '0 4px 20px rgba(0,0,0,0.55)' }}>
          <h3 className="text-xl font-bold mb-5" style={{ color: C.goldLight, fontFamily: "'Tiro Devanagari Hindi', serif" }}>आगे की प्रक्रिया</h3>
          <div className="space-y-4">
            {[
              { n: 1, title: 'साधना शुरू', desc: 'माता जी आज रात्रि से आपके नाम से गुप्त साधना शुरू करेंगी' },
              { n: 2, title: 'साधना पूर्ण', desc: '6-7 दिन में साधना पूर्ण होगी' },
              { n: 3, title: 'यंत्र सिद्धि', desc: 'विशेष यंत्र सिद्ध किया जाएगा' },
              { n: 4, title: 'प्रसाद डिलीवरी', desc: '10-15 दिन में यंत्र आपके पते पर भेजा जाएगा' },
            ].map((s) => (
              <div key={s.n} className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0" style={{ background: C.maroon, color: C.goldSoft, fontFamily: "'Tiro Devanagari Hindi', serif" }}>{s.n}</div>
                <div>
                  <h4 className="font-bold" style={{ color: C.goldLight }}>{s.title}</h4>
                  <p className="text-sm leading-relaxed" style={{ color: C.text }}>{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Important note */}
        <section className="rounded-2xl p-6 mb-6" style={{ background: 'rgba(201,162,58,0.08)', border: `1px solid ${C.border}` }}>
          <h3 className="text-lg font-bold mb-3" style={{ color: C.goldLight, fontFamily: "'Tiro Devanagari Hindi', serif" }}>! महत्वपूर्ण निर्देश</h3>
          <ul className="space-y-2 text-sm leading-relaxed" style={{ color: C.text }}>
            <li>◆ कृपया धैर्य रखें और पूर्ण श्रद्धा से प्रतीक्षा करें</li>
            <li>◆ साधना के दौरान किसी को न बताएं</li>
            <li>◆ नकारात्मक विचारों से दूर रहें</li>
            <li>◆ यंत्र प्राप्त होने पर धारण करने की विधि बताई जाएगी</li>
          </ul>
        </section>

        {/* Address Form */}
        {!addressSubmitted ? (
          <section className="rounded-2xl p-7 mb-6" style={{ background: C.creamWarm, border: `1px solid ${C.border}`, boxShadow: '0 4px 20px rgba(0,0,0,0.55)' }} data-testid="address-form">
            <h3 className="text-xl font-bold mb-4" style={{ color: C.goldLight, fontFamily: "'Tiro Devanagari Hindi', serif" }}>यंत्र डिलीवरी के लिए पता भरें</h3>
            <form onSubmit={handleAddressSubmit} className="space-y-4">
              <div>
                <label className="block text-sm mb-1 font-bold" style={{ color: C.goldLight }}>पूरा पता *</label>
                <textarea value={address.address} onChange={(e) => setAddress({ ...address, address: e.target.value })} required rows={3} data-testid="address-input" className="w-full px-4 py-3 rounded-xl focus:outline-none" style={inputStyle} placeholder="घर का नंबर, गली, मोहल्ला..." />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1 font-bold" style={{ color: C.goldLight }}>शहर *</label>
                  <input type="text" value={address.city} onChange={(e) => setAddress({ ...address, city: e.target.value })} required data-testid="city-input" className="w-full px-4 py-3 rounded-xl focus:outline-none" style={inputStyle} placeholder="शहर का नाम" />
                </div>
                <div>
                  <label className="block text-sm mb-1 font-bold" style={{ color: C.goldLight }}>राज्य *</label>
                  <input type="text" value={address.state} onChange={(e) => setAddress({ ...address, state: e.target.value })} required data-testid="state-input" className="w-full px-4 py-3 rounded-xl focus:outline-none" style={inputStyle} placeholder="राज्य" />
                </div>
              </div>
              <div>
                <label className="block text-sm mb-1 font-bold" style={{ color: C.goldLight }}>पिनकोड *</label>
                <input type="text" value={address.pincode} onChange={(e) => setAddress({ ...address, pincode: e.target.value })} required pattern="[0-9]{6}" data-testid="pincode-input" className="w-full px-4 py-3 rounded-xl focus:outline-none" style={inputStyle} placeholder="6 अंकों का पिनकोड" />
              </div>
              <button type="submit" disabled={submitting} data-testid="address-submit-button" className="w-full font-extrabold py-4 rounded-full transition-all disabled:opacity-60" style={{ background: 'linear-gradient(135deg, #1C0606 0%, #6E0F0F 100%)', color: '#F5E7C8', border: `1px solid ${C.gold}`, boxShadow: '0 6px 24px rgba(201,162,58,0.45)', fontFamily: "'Tiro Devanagari Hindi', serif" }}>
                {submitting ? 'Saving...' : 'पता सेव करें'}
              </button>
            </form>
          </section>
        ) : (
          <section className="rounded-2xl p-6 mb-6 text-center" style={{ background: 'rgba(201,162,58,0.08)', border: `2px solid ${C.success}` }} data-testid="address-saved">
            <div className="text-4xl mb-3" style={{ color: C.success }}>✓</div>
            <h3 className="text-xl font-bold mb-2" style={{ color: C.success }}>पता सेव हो गया</h3>
            <p style={{ color: C.success }}>यंत्र इसी पते पर भेजा जाएगा</p>
          </section>
        )}

        <div className="rounded-xl p-4 mt-10 text-center" style={{ background: 'rgba(201,162,58,0.05)', border: `1px solid ${C.border}` }}>
          <p className="text-sm" style={{ color: C.textMuted }}>❖ अघोर विधि से कार्य होता है | पूरी श्रद्धा-विश्वास हो तभी आएं ❖</p>
        </div>
      </main>

      <footer className="py-8 text-center" style={{ background: `linear-gradient(180deg, ${C.maroon} 0%, ${C.maroonDark} 100%)`, borderTop: `2px solid ${C.gold}` }}>
        <div className="text-2xl mb-2" style={{ color: C.gold }}>ॐ</div>
        <p className="font-bold tracking-widest" style={{ color: C.goldSoft }}>जय माता मलिहा</p>
      </footer>
    </div>
  );
};

export default ThankYouPage;
